<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue'
import type { CSSProperties } from 'vue'

import { FLASHCARD_INTERACTIVE } from '../../flashcard/context'

type CheckboxItem = {
  id: string
  checked: boolean
}

type ToggleHandler = (index: number, nextValue: boolean, values: boolean[]) => void

const props = defineProps<{
  items: CheckboxItem[]
  direction: 'vertical' | 'horizontal'
  gap: number
  boxSize: number
  style?: CSSProperties
  /** Optional per-element override. If unset, the Flashcard's global tick style applies. */
  tickStyle?: 'fill' | 'x' | 'haken'
  themeDark: boolean
  backgroundColor: string
  /** Optional per-element override; when false, the checkbox group becomes read-only. */
  interactive?: boolean
  onToggle?: ToggleHandler
}>()

const isInteractive = inject(
  FLASHCARD_INTERACTIVE,
  computed(() => true),
)
const isReadOnly = computed(() => props.interactive === false || isInteractive.value === false)
const isCanvas = computed(() => isReadOnly.value === true)

const colorTokens = computed(() => ({
  '--checkbox-border-color': props.themeDark ? 'rgba(248, 250, 252, 0.6)' : 'rgba(15, 23, 42, 0.5)',
  '--checkbox-active-border': props.themeDark
    ? 'rgba(248, 250, 252, 0.95)'
    : 'color-mix(in srgb, var(--pl-color-primary, #4338ca), #000 12%)',
  '--checkbox-bg': props.backgroundColor,
  '--checkbox-active-bg': props.themeDark
    ? 'rgba(248, 250, 252, 0.2)'
    : 'color-mix(in srgb, var(--pl-color-primary, #4338ca) 22%, transparent)',
  '--checkbox-indicator': props.themeDark
    ? 'rgba(248, 250, 252, 0.9)'
    : 'var(--pl-color-primary, #4338ca)',
}))

const wrapperStyle = computed<Record<string, string | number>>(() => ({
  ...(props.style as Record<string, string | number> | undefined),
  display: 'flex',
  flexDirection: props.direction === 'horizontal' ? 'row' : 'column',
  alignItems: 'flex-start',
  gap: `${props.gap}px`,
  '--checkbox-size': `${props.boxSize}px`,
  backgroundColor: props.backgroundColor,
  colorScheme: 'light',
  ...colorTokens.value,
}))

const tickStyleClass = computed(() =>
  props.tickStyle ? `pfc-checkbox-tick--${props.tickStyle}` : undefined,
)

const itemsState = ref(props.items.map((item) => ({ ...item })))

watch(
  () => props.items,
  (next) => {
    itemsState.value = next.map((item) => ({ ...item }))
  },
  { deep: true },
)

const toggleAt = (index: number) => {
  if (isCanvas.value) {
    return
  }
  const item = itemsState.value[index]
  if (!item) {
    return
  }
  const nextValue = !item.checked
  itemsState.value = itemsState.value.map((entry, entryIndex) =>
    entryIndex === index ? { ...entry, checked: nextValue } : entry,
  )
  props.onToggle?.(
    index,
    nextValue,
    itemsState.value.map((entry) => entry.checked),
  )
}
const handlePointerDown = (event: PointerEvent) => {
  if (isCanvas.value) return
  event.stopPropagation()
}

const handleClick = (event: MouseEvent, index: number) => {
  if (isCanvas.value) return
  event.preventDefault()
  event.stopPropagation()
  toggleAt(index)
}
</script>

<template>
  <div
    class="checkbox-group"
    :class="tickStyleClass"
    :style="wrapperStyle"
    role="group"
    data-darkreader-ignore
    data-darkreader-ignore-bgcolor
    data-darkreader-inline-bgcolor
  >
    <button
      v-for="(item, index) in itemsState"
      :key="item.id"
      class="checkbox"
      type="button"
      :aria-pressed="item.checked"
      :data-checked="item.checked"
      @click="handleClick($event, index)"
      @pointerdown="handlePointerDown"
      data-darkreader-ignore
      data-darkreader-ignore-bgcolor
      data-darkreader-inline-bgcolor
    >
      <span
        class="indicator"
        data-darkreader-ignore
        data-darkreader-ignore-bgcolor
        data-darkreader-inline-bgcolor
      />
    </button>
  </div>
</template>

<style scoped>
.checkbox-group {
  pointer-events: auto;
  color-scheme: light;
}

.checkbox-group,
.checkbox-group * {
  filter: none !important;
  mix-blend-mode: normal;
}

.checkbox {
  box-sizing: border-box;
  width: var(--checkbox-size, 16px);
  height: var(--checkbox-size, 16px);
  min-width: 0 !important;
  min-height: 0 !important;
  max-width: var(--checkbox-size, 16px);
  max-height: var(--checkbox-size, 16px);
  border-style: solid;
  border-width: clamp(1px, calc(var(--checkbox-size, 16px) * 0.12), 2px);
  border-color: var(--checkbox-border-color, rgba(15, 23, 42, 0.5));
  border-radius: clamp(2px, calc(var(--checkbox-size, 16px) * 0.25), 4px);
  background: var(--checkbox-bg, rgba(255, 255, 255, 0.7));
  background-color: var(--checkbox-bg, rgba(255, 255, 255, 0.7)) !important;
  border-color: var(--checkbox-border-color, rgba(15, 23, 42, 0.5)) !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  line-height: 0;
  font-size: 0;
  transition:
    border-color 150ms ease,
    background 150ms ease;
  cursor: pointer;
  padding: 0;
  outline: none;
}

.checkbox[data-checked='true'] {
  background: var(
    --checkbox-active-bg,
    color-mix(in srgb, var(--pl-color-primary, #4338ca) 22%, transparent)
  );
  background-color: var(
    --checkbox-active-bg,
    color-mix(in srgb, var(--pl-color-primary, #4338ca) 22%, transparent)
  ) !important;
  border-color: var(
    --checkbox-active-border,
    color-mix(in srgb, var(--pl-color-primary, #4338ca), #000 12%)
  ) !important;
}

.checkbox:focus-visible {
  box-shadow: 0 0 0 2px var(--pl-focus-ring, rgba(67, 56, 202, 0.35));
}

.indicator {
  width: calc(var(--checkbox-size, 16px) * 0.55);
  height: calc(var(--checkbox-size, 16px) * 0.55);
  border-radius: 2px;
  background: var(--checkbox-indicator, var(--pl-color-primary, #4338ca));
  background-color: var(--checkbox-indicator, var(--pl-color-primary, #4338ca)) !important;
  position: relative;
  opacity: 0;
  transition: opacity 150ms ease;
}

/* X tick style */
:deep(.pfc-checkbox-tick--x) .indicator,
.pfc-checkbox-tick--x .indicator {
  background: transparent;
  background-color: transparent !important;
  border-radius: 0;
}

:deep(.pfc-checkbox-tick--x) .indicator::before,
:deep(.pfc-checkbox-tick--x) .indicator::after,
.pfc-checkbox-tick--x .indicator::before,
.pfc-checkbox-tick--x .indicator::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 140%;
  height: clamp(1.5px, calc(var(--checkbox-size, 16px) * 0.11), 2.5px);
  background: var(--checkbox-indicator, var(--pl-color-primary, #4338ca));
  border-radius: 999px;
  transform-origin: center;
}

:deep(.pfc-checkbox-tick--x) .indicator::before,
.pfc-checkbox-tick--x .indicator::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

:deep(.pfc-checkbox-tick--x) .indicator::after,
.pfc-checkbox-tick--x .indicator::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

/* Haken (checkmark) tick style */
:deep(.pfc-checkbox-tick--haken) .indicator,
.pfc-checkbox-tick--haken .indicator {
  background: transparent;
  background-color: transparent !important;
  border-radius: 0;
}

:deep(.pfc-checkbox-tick--haken) .indicator::before,
.pfc-checkbox-tick--haken .indicator::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  background: transparent !important;
  background-color: transparent !important;
  width: 70%;
  height: 120%;
  border-right: clamp(1.5px, calc(var(--checkbox-size, 16px) * 0.11), 2.5px) solid
    var(--checkbox-indicator, var(--pl-color-primary, #4338ca));
  border-bottom: clamp(1.5px, calc(var(--checkbox-size, 16px) * 0.11), 2.5px) solid
    var(--checkbox-indicator, var(--pl-color-primary, #4338ca));
  transform: translate(-50%, -60%) rotate(40deg);
}

/* If this checkbox-group explicitly sets a tickStyle, ensure it overrides any global style. */
.checkbox-group.pfc-checkbox-tick--fill .indicator {
  background: var(--checkbox-indicator, var(--pl-color-primary, #4338ca));
  background-color: var(--checkbox-indicator, var(--pl-color-primary, #4338ca)) !important;
  border-radius: 2px;
}

.checkbox-group.pfc-checkbox-tick--fill .indicator::before,
.checkbox-group.pfc-checkbox-tick--fill .indicator::after {
  content: none;
}

.checkbox-group.pfc-checkbox-tick--x .indicator::before,
.checkbox-group.pfc-checkbox-tick--x .indicator::after {
  border: none;
}

.checkbox-group.pfc-checkbox-tick--haken .indicator::after {
  content: none;
}

.checkbox[data-checked='true'] .indicator {
  opacity: 1;
}
</style>
